import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Scene3D from "./Scene3D";
import { KineticLines } from "./Reveal";
import { IMAGES } from "../../data";

export default function Hero() {
  const ref = useRef(null);
  const navigate = useNavigate();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yText = useTransform(scrollYProgress, [0, 1], [0, -140]);
  const scaleBg = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const goBook = () => {
    navigate("/book");
    window.scrollTo({ top: 0 });
  };

  return (
    <section
      id="hero"
      ref={ref}
      className="relative min-h-screen w-full overflow-hidden bg-ink"
      data-testid="hero-section"
    >
      {/* Blended, cinematic multi-image hero composition */}
      <motion.div style={{ scale: scaleBg }} className="absolute inset-0">
        <img
          src={IMAGES.heroClass}
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-45"
        />
        <img
          src={IMAGES.heroFloor}
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-30"
          style={{
            maskImage:
              "linear-gradient(to right, transparent 0%, black 45%, black 100%)",
            WebkitMaskImage:
              "linear-gradient(to right, transparent 0%, black 45%, black 100%)",
          }}
        />
        {/* Dark overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/55 to-ink" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/80 via-ink/30 to-transparent" />
      </motion.div>

      {/* 3D logo layer */}
      <motion.div
        style={{ opacity: fade }}
        className="absolute inset-0 md:left-[40%] pointer-events-none"
      >
        <Scene3D />
      </motion.div>

      {/* Content */}
      <motion.div
        style={{ y: yText }}
        className="relative z-10 mx-auto max-w-[1400px] px-6 lg:px-10 min-h-screen flex flex-col justify-center pt-24"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 1 }}
          className="font-body text-xs md:text-sm uppercase tracking-[0.35em] text-teal mb-6 flex items-center gap-3"
        >
          <span className="h-px w-10 bg-teal inline-block" />
          St. Augustine, FL · Muay Thai · Christ-Centered
        </motion.p>

        <h1 className="font-display font-700 uppercase leading-[0.85] tracking-tighter text-white text-6xl sm:text-7xl md:text-8xl lg:text-[9vw]">
          <KineticLines lines={["War Room"]} start={0.35} trigger="load" />
          <KineticLines
            lines={["Performance"]}
            start={0.5}
            trigger="load"
            lineClassName="text-stroke-pink"
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.9 }}
          className="mt-8 max-w-xl font-body text-base md:text-lg text-neutral-300 leading-relaxed"
        >
          Authentic Muay Thai in a clean, faith-driven community. St. Augustine&apos;s
          only authentic Muay Thai school — sharpen your craft, build your body,
          and forge discipline that lasts.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={goBook}
            data-testid="hero-book-class-btn"
            className="font-display uppercase tracking-wide text-base px-8 py-4 bg-hotpink text-white hover:bg-white hover:text-ink transition-colors duration-300 rounded-sm hover:drop-shadow-[0_0_20px_rgba(255,20,147,0.5)]"
          >
            Book Your First Free Class
          </button>
          <button
            onClick={() =>
              document.getElementById("classes")?.scrollIntoView({ behavior: "smooth" })
            }
            data-testid="hero-explore-btn"
            className="font-display uppercase tracking-wide text-base px-8 py-4 border border-white/20 text-white hover:border-teal hover:text-teal transition-colors duration-300 rounded-sm"
          >
            Explore Programs
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-neutral-400"
      >
        <span className="font-body text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
        >
          <ArrowDown size={18} />
        </motion.div>
      </motion.div>
    </section>
  );
}
